import {
  ANALYTICS_PROPERTY,
  CreatePaymentIntentEndpoint,
  PaymentIntentRequestTrigger,
} from '@kouto/types';
import { useAppState, useBrandId } from 'AppProvider';
import { analyticsManager, checkoutDiagnostics } from 'features/analytics';
import { PaymentIntentError } from 'features/ShoppingCart/types';
import useCartItems from 'hooks/useCartItems';
import useIsDocumentVisible from 'hooks/useIsDocumentVisible';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';
import * as types from 'types/booking';
import { ICartState } from 'types/cart';
import { PaymentMethod } from 'types/payment';
import { sleep } from 'utils';
import { z } from 'zod';
import { post } from '../../../../../utils/axios';
import getCreatePaymentIntentEndpointUrl from '../../../utils/getCreatePaymentIntentEndpointUrl';
import { getCartItemsData } from '../checkoutUtils';

type PaymentIntentResponse = {
  data: z.infer<typeof CreatePaymentIntentEndpoint.responseSchema>;
};

type PaymentIntentRequestData = ReturnType<typeof getCartItemsData>;
type PaymentIntentKey = [string, PaymentIntentRequestData, number];
type SentPaymentIntentRequest = { revision: number; url: string };

// Revision bumps arrive in bursts (cross-tab sync + discount/credit restores),
// so wait this long for the burst to settle before sending.
const PAYMENT_INTENT_DEBOUNCE_SECONDS = 0.3;

const derivePaymentIntentTrigger = (
  previous: SentPaymentIntentRequest | null,
  current: SentPaymentIntentRequest,
  hadLocalChange: boolean,
): PaymentIntentRequestTrigger => {
  if (!previous) return PaymentIntentRequestTrigger.INITIAL;
  if (previous.url !== current.url)
    return PaymentIntentRequestTrigger.CART_CHANGED;
  if (previous.revision === current.revision)
    return PaymentIntentRequestTrigger.REFRESH;
  return hadLocalChange
    ? PaymentIntentRequestTrigger.LOCAL_CHANGE
    : PaymentIntentRequestTrigger.SYNCED_CHANGE;
};

const usePaymentIntent = (
  selectedPaymentMethod: string,
): {
  paymentIntent: string | undefined;
  paymentIntentSecret: string | undefined | null;
  isLoading: boolean;
  isValidating: boolean;
  error: PaymentIntentError | null;
  refreshPaymentIntent: () => void;
  freezePaymentIntent: () => void;
  unfreezePaymentIntent: () => void;
} => {
  const brandId = useBrandId();
  const { cartId, cartItems, paymentIntentRevision } = useCartItems();
  const { discountCode } = useAppState(
    (state: Record<string, unknown>) => state.discountCode,
  );
  const { creditCodes } = useAppState(
    (state: Record<string, unknown>) => state.creditCodes,
  );
  const isLastChangeRestored = useAppState(
    (state: { cart: ICartState }) => !!state.cart.isLastChangeRestored,
  );

  const defaultCartItemsData = useMemo(() => {
    return getCartItemsData({ cartItems, discountCode, creditCodes });
  }, [cartItems, discountCode, creditCodes]);

  const isHyattPaymentOption = useMemo(
    () => selectedPaymentMethod === PaymentMethod.HYATT_POINTS,
    [selectedPaymentMethod],
  );
  const shouldFetch =
    !!cartId && !!defaultCartItemsData && !isHyattPaymentOption;

  const isDocumentVisible = useIsDocumentVisible();
  const abortControllerRef = useRef<AbortController | null>(null);
  const checkoutLockedRef = useRef(false);
  const lastResponseRef = useRef<PaymentIntentResponse | null>(null);
  const latestRequestIdRef = useRef(0);
  const inFlightRequestRef = useRef<Promise<unknown>>(Promise.resolve());
  const visibleKeyRef = useRef<PaymentIntentKey | null>(null);
  const lastSentRequestRef = useRef<SentPaymentIntentRequest | null>(null);
  const hadLocalChangeRef = useRef(false);

  // Another tab's echo of this tab's change can land before the debounced
  // request is sent, so remember any local change since the last request.
  useEffect(() => {
    if (!isLastChangeRestored) {
      hadLocalChangeRef.current = true;
    }
  }, [isLastChangeRestored, paymentIntentRevision]);

  const livePaymentIntentKey: PaymentIntentKey | null = shouldFetch
    ? [
        getCreatePaymentIntentEndpointUrl(brandId, cartId),
        defaultCartItemsData,
        paymentIntentRevision,
      ]
    : null;

  // A background tab keeps the key it had when it was last visible, so cart
  // changes synced from other tabs don't send requests until the guest returns
  // to it. Keeping the key (not nulling it) keeps the current payment intent.
  if (isDocumentVisible) {
    visibleKeyRef.current = livePaymentIntentKey;
  }
  const paymentIntentKey = visibleKeyRef.current;

  const { data, isLoading, isValidating, error, mutate } =
    useSWR<PaymentIntentResponse>(
      paymentIntentKey,
      async ([
        url,
        cartItemsData,
        requestPaymentIntentRevision,
      ]: PaymentIntentKey) => {
        if (checkoutLockedRef.current && lastResponseRef.current) {
          return lastResponseRef.current;
        }

        // Aborting a request doesn't stop the API: it still waits on the
        // cart's lock while holding a DB connection, and enough of them
        // exhaust the pool. So never overlap requests, and send only the
        // latest one once a burst of revision bumps has settled. Superseded
        // keys are never shown, so their rejection is discarded.
        latestRequestIdRef.current += 1;
        const requestId = latestRequestIdRef.current;
        const isSuperseded = () => requestId !== latestRequestIdRef.current;

        await sleep(PAYMENT_INTENT_DEBOUNCE_SECONDS);
        if (isSuperseded())
          throw new Error('Superseded payment intent request');
        await inFlightRequestRef.current;
        if (isSuperseded())
          throw new Error('Superseded payment intent request');

        if (checkoutLockedRef.current && lastResponseRef.current) {
          return lastResponseRef.current;
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const sentRequest = { revision: requestPaymentIntentRevision, url };
        const trigger = derivePaymentIntentTrigger(
          lastSentRequestRef.current,
          sentRequest,
          hadLocalChangeRef.current,
        );
        lastSentRequestRef.current = sentRequest;
        hadLocalChangeRef.current = false;
        checkoutDiagnostics.recordPaymentIntentRequest({
          cartId,
          isLastChangeRestored,
          paymentIntentRevision: requestPaymentIntentRevision,
        });

        const request = post<
          PaymentIntentResponse,
          {
            clientContext: { sessionId: string; trigger: string };
            data: typeof cartItemsData;
            paymentIntentRevision: number;
          }
        >(
          url,
          {
            // Lets API logs be joined to this page load's analytics session.
            clientContext: {
              sessionId:
                analyticsManager.getAppData()[ANALYTICS_PROPERTY.SessionId] ??
                '',
              trigger,
            },
            data: cartItemsData,
            paymentIntentRevision: requestPaymentIntentRevision,
          },
          { signal: controller.signal },
        )
          .then((response) => {
            if (!checkoutLockedRef.current) {
              lastResponseRef.current = response;
            }
            return lastResponseRef.current ?? response;
          })
          .catch((err) => {
            if (!controller.signal.aborted) {
              checkoutDiagnostics.recordPaymentIntentFailure({
                errorType: err?.response?.data?.type,
                status: err?.response?.status,
              });
            }
            throw err;
          });
        inFlightRequestRef.current = request.catch(() => undefined);
        return request;
      },
      {
        revalidateOnFocus: false,
        shouldRetryOnError: false,
      },
    );

  const paymentIntent = data?.data.paymentIntentSecret?.slice(0, 27);

  const { experiences, paymentMethod, resourceGroups } =
    error?.response?.data?.errors ?? {};

  const formattedError = error
    ? {
        type: types.CREATE_BULK_BOOKING_FAILURE,
        error: error?.response?.data?.detail ?? 'Unknown error',
        errorType: error?.response?.data?.type,
        payload: {
          validationMessages: {},
          responseStatus: error?.response?.status,
        },
        cartItemsError: paymentMethod?.error
          ? [paymentMethod.error]
          : experiences || resourceGroups,
      }
    : null;

  const freezePaymentIntent = useCallback(() => {
    checkoutLockedRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const unfreezePaymentIntent = useCallback(() => {
    checkoutLockedRef.current = false;
  }, []);

  return {
    paymentIntent,
    paymentIntentSecret: data?.data.paymentIntentSecret,
    isLoading,
    isValidating,
    error: formattedError as PaymentIntentError,
    refreshPaymentIntent: () => mutate(),
    freezePaymentIntent,
    unfreezePaymentIntent,
  };
};

export default usePaymentIntent;
